// Single source of truth for classifying where a visit came from.
// Used by the browser (via src/lib/trafficSource.ts) and by track-visit,
// so the dashboard and the stored rows always agree.

export type TrafficSource = { source: string; medium: string; referrerHost: string };

export const CONVERSION_EVENTS = ["signup", "inquiry", "contact", "listing_created"] as const;
export type ConversionEvent = (typeof CONVERSION_EVENTS)[number];

const OWN_HOSTS = ["dirtmerchantmarketplace.com", "dirtmerchantmarketplace.lovable.app", "localhost"];

function clean(v: string | null | undefined, max = 60): string {
  return (v ?? "").toLowerCase().replace(/[^a-z0-9._\-]/g, "").slice(0, max);
}

function hostOf(referrer: string): string {
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
}

export function isGoogleHost(host: string): boolean {
  return /(^|\.)google\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(host);
}

/**
 * Rules (in order):
 * 1. utm_source/utm_medium win when present.
 * 2. gclid → google / paid.
 * 3. google.* referrer → google / organic.
 * 4. other external referrer → <host> / referral.
 * 5. otherwise (none or own site) → direct / none.
 */
export function classifyTraffic(search: string, referrer: string): TrafficSource {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const host = hostOf(referrer);
  const referrerHost = clean(host, 120);
  const utmSource = clean(params.get("utm_source"));
  const utmMedium = clean(params.get("utm_medium"));

  if (utmSource) {
    const medium = ["cpc", "ppc", "paid"].includes(utmMedium) ? "paid" : utmMedium || "referral";
    return { source: utmSource, medium, referrerHost };
  }
  if (params.get("gclid")) return { source: "google", medium: "paid", referrerHost };
  if (host && isGoogleHost(host)) return { source: "google", medium: "organic", referrerHost };
  if (host && !OWN_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) {
    return { source: referrerHost, medium: "referral", referrerHost };
  }
  return { source: "direct", medium: "none", referrerHost: "" };
}
